import FormCreateOrEditTransactionBodyInputCategory from './FormCreateOrEditTransactionBodyInputCategory';
import FormCreateOrEditTransactionBodyInputEmployeeOrInvestor from './FormCreateOrEditTransactionBodyInputEmployeeOrInvestor';
import FormCreateOrEditTransactionBodyInputUser from './FormCreateOrEditTransactionBodyInputUser';
import { ControlledInput, ControlledSelect, ControlledTextarea } from '@/components/controlled';
import { TransactionTypeEnum, TransactionCategoryEnum } from '@/enums';
import { cn } from '@/lib/utils';

import { useFormContext, useWatch } from 'react-hook-form';
import { getSelectOptions, nameof } from '@/functions';
import { LabelRequired } from '@/components/hoc';
import { Label } from '@/components/ui';
import { ENABLE_MAX_TRANSACTION_AGE_VALIDATION, MAX_TRANSACTION_AGE_DAYS } from '@/constants';
import { TFormData } from '..';
import dayjs from 'dayjs';
import { FileText, CreditCard, AlignLeft } from 'lucide-react';

const FormCreateOrEditTransactionBody = () => {

  const { control, formState: { errors } } = useFormContext<TFormData>();
  const type = useWatch({ control, name: 'type' });
  const category = useWatch({ control, name: 'category' });
  const transactionId = useWatch({ control, name: '_id' });
  const isOtherIncome = category === TransactionCategoryEnum.OTHER_INCOME;
  /* the API reads the picked day as UTC midnight and checks it against the UTC month, so the form does the same */
  const utcToday = new Date().toISOString().slice(0, 10);
  const utcMonthStart = `${utcToday.slice(0, 7)}-01`;
  const isDescriptionRequired = [
    TransactionCategoryEnum.MARKETING,
    TransactionCategoryEnum.MARKETING_GOVOIR,
    TransactionCategoryEnum.ADMINISTRATION,
    TransactionCategoryEnum.INFRASTRUCTURE,
    TransactionCategoryEnum.OTHER_INCOME
  ].includes(category as TransactionCategoryEnum);

  const SectionHeader = ({ icon: Icon, title, className = "" }: { icon: any, title: string, className?: string }) => (
    <div className={`hidden md:flex items-center space-x-2 pb-2 border-b border-border/40 mb-2 ${className}`}>
      <div className="p-1 bg-primary/10 rounded-md text-primary">
        <Icon size={14} />
      </div>
      <h3 className="text-xs font-semibold tracking-wide uppercase text-foreground/80">{title}</h3>
    </div>
  );

  return (
    <div className='py-2 md:py-4 space-y-3 md:space-y-8 animate-in fade-in duration-500'>

      <section>
        <SectionHeader icon={FileText} title="Informations Générales" />
        <div className='grid grid-cols-2 gap-3 md:gap-6'>

          {!transactionId && (
            <>
              <FormCreateOrEditTransactionBodyInputCategory />

              <div className='flex flex-col gap-1 md:gap-1.5'>
                <LabelRequired className="text-[11px] md:text-xs">Type</LabelRequired>
                <ControlledSelect
                  control={control}
                  rules={{ required: 'Requis' }}
                  name={nameof<TFormData>('type')}
                  renderClassNameItem={
                    l => l.value === TransactionTypeEnum.INCOME
                      ? 'text-emerald-500'
                      : 'text-red-500'
                  }
                  placeholder='Selectionner le type'
                  options={getSelectOptions('TransactionTypeEnum')} />
              </div>
            </>
          )}
        </div>
      </section>

      <section>
        <SectionHeader icon={CreditCard} title="Détails Financiers" />

        <div className="mb-4 md:mb-6 space-y-4">
          <FormCreateOrEditTransactionBodyInputEmployeeOrInvestor />

          {!transactionId && (
            <div className={cn(
              "grid grid-cols-1 gap-4",
              category === TransactionCategoryEnum.IN_APP_PURCHASE && "md:grid-cols-2"
            )}>
              <FormCreateOrEditTransactionBodyInputUser />

              {category === TransactionCategoryEnum.IN_APP_PURCHASE && (
                <div className='flex flex-col gap-1 md:gap-1.5 animate-in fade-in slide-in-from-top-1 duration-300'>
                  <LabelRequired className="text-[11px] md:text-xs">Référence de paiement</LabelRequired>
                  <ControlledInput
                    control={control}
                    rules={{ required: 'La référence de paiement est requise' }}
                    name="data.paymentRef"
                    placeholder={'GPA.3392...'}
                    className="bg-muted/30 focus:bg-background transition-colors h-9 md:h-10 font-mono" />
                </div>
              )}
            </div>
          )}
        </div>

        {!transactionId && (
          <div className='grid grid-cols-2 gap-3 md:gap-6'>
            <div className='flex flex-col gap-1 md:gap-1.5'>
              <LabelRequired className="text-[11px] md:text-xs">Montant (EUR)</LabelRequired>
              <ControlledInput
                control={control}
                rules={{
                  required: 'Requis',
                  min: { value: 0.01, message: 'Le montant doit être supérieur à 0' }
                }}
                type='number'
                step='0.01'
                name={nameof<TFormData>('amountEur')}
                placeholder='0.00'
                className="bg-muted/30 focus:bg-background transition-colors h-9 md:h-10 font-mono"
                onFocus={
                  e => e.target.value?.toString() === '' &&
                    e.target.select()
                } />
            </div>

            <div className='flex flex-col gap-1 md:gap-1.5'>
              <LabelRequired className="text-[11px] md:text-xs">Date</LabelRequired>
              <ControlledInput
                control={control}
                rules={{
                  required: 'Requis',
                  validate: value => {
                    if (isOtherIncome) {
                      return (value >= utcMonthStart && value <= utcToday) || 'Un revenu doit être daté du mois en cours, pas dans le futur';
                    }
                    const date = dayjs(value);
                    const now = dayjs();
                    if (date.isAfter(now)) {
                      return 'La date ne peut pas être dans le futur';
                    }
                    if (ENABLE_MAX_TRANSACTION_AGE_VALIDATION && now.diff(date, 'day') > MAX_TRANSACTION_AGE_DAYS) {
                      return `La date ne peut pas être plus vieille de plus de ${MAX_TRANSACTION_AGE_DAYS} jours`;
                    }
                    return true;
                  }
                }}
                type='date'
                min={isOtherIncome ? utcMonthStart : undefined}
                max={isOtherIncome ? utcToday : undefined}
                name={nameof<TFormData>('occurredAt')}
                className="bg-muted/30 focus:bg-background transition-colors h-9 md:h-10" />
              {errors.occurredAt?.message && <p className="text-xs text-red-500">{errors.occurredAt.message}</p>}
            </div>
          </div>
        )}
      </section>

      <section>
        <SectionHeader icon={AlignLeft} title="Compléments" />
        <div className='flex flex-col gap-1 md:gap-1.5'>
          {isDescriptionRequired ? (
            <LabelRequired className="text-[11px] md:text-xs">Description</LabelRequired>
          ) : (
            <Label className="text-[11px] md:text-xs font-medium text-foreground/80">
              Description <span className="text-muted-foreground font-normal">{(type === TransactionTypeEnum.INCOME) ? '' : '(min 20 caractères pour Marketing, Marketing Govoir, Admin, Infrastructure)'}</span>
            </Label>
          )}
          <ControlledTextarea
            control={control}
            name={nameof<TFormData>('description')}
            rules={{
              validate: value => {
                if (!value || value.length === 0) {
                  return isDescriptionRequired ? 'La description est requise' : true;
                }
                if (value.length < 20) {
                  return 'La description doit contenir au moins 20 caractères';
                }
                return true;
              }
            }}
           placeholder={'Ajouter une description détaillée...'}
            className="bg-muted/30 focus:bg-background transition-colors min-h-[80px] md:min-h-[100px]" />
          {errors.description?.message && <p className="text-xs text-red-500">{errors.description.message}</p>}
        </div>
      </section>

    </div>
  );

};

export default FormCreateOrEditTransactionBody;


